import React, { useEffect, useMemo, useState } from 'react';

import {
  useGetGroup,
  useGetStream,
  useUpdateStream,
} from '@vlabs/api/artemy/client';
import type { CreateStreamRequest, StreamAnalytics } from '@vlabs/api/artemy/model';
import {
  buildDefaultStreamAnalytics,
  SourceGeoposition,
  getAnalyticsTypeLabel,
  getStreamAnalyticsParamsSyncStatus,
  getStreamSettingsSyncStatus,
  nextAnalyticsLabel,
  normalizeStreamAnalyticsRecordItem,
  parseAnalyticsLabel,
  stripEmpty,
  toFormStreamSettings,
  toRequestStreamSettings,
} from '@vlabs/monokit/entities/sources';
import { FacestreamAnalytic } from '@vlabs/monokit/entities/videoanalytics';
import { UnsavedChangesModal } from '@vlabs/monokit/features/unsaved-changes-modal';
import { RegionsEditModal } from '@vlabs/monokit/pages/regions-edit-modal';
import type { TEditData, TRectangle, TRotationDeg } from '@vlabs/monokit/pages/regions-edit-modal/types';
import { getApiErrorInfo, parseValidationDetail, setServerFieldErrors } from '@vlabs/monokit/shared/lib/errors';
import { useStreamGroupId } from '@vlabs/monokit/shared/lib/use-stream-group-id';
import { useStreamIdFromLocation } from '@vlabs/monokit/shared/lib/use-stream-id-from-location';
import { useUnsavedChangesToast } from '@vlabs/monokit/shared/lib/use-unsaved-changes-toast';
import { useStreamRestream } from '@vlabs/monokit/shared/lib/video';
import { Page, PageContent } from '@vlabs/monokit/shared/ui/page';
import { SideNavLayout } from '@vlabs/monokit/shared/ui/side-nav-layout';
import { SourceStreamGeneralInfo } from '@vlabs/monokit/widgets/source-stream-general-info';
import type { StreamSettingsFormValues } from '@vlabs/monokit/widgets/source-stream-general-info/types';
import {
  SourceStreamLastFrame,
  SourceStreamLegend,
} from '@vlabs/monokit/widgets/source-stream-last-frame';
import { SourceStreamSettings } from '@vlabs/monokit/widgets/source-stream-settings';
import {
  mapErrorsToSections,
  SourceStreamSettingsNav,
} from '@vlabs/monokit/widgets/source-stream-settings-nav';
import type {
  SourceStreamAnalyticsItem,
  SourceStreamSection,
} from '@vlabs/monokit/widgets/source-stream-settings-nav';
import Loader from '@vlabs/shared/legacy-components/loader/Loader';
import { useFieldArray, FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useSourceStreamPageTranslation } from './i18n';
import st from './SourceStreamPage.module.sass';
import { SourceStreamPageHeader } from './SourceStreamPageHeader';
import { routes } from '../../app/routes';

const UNSAVED_TOAST_ID = 'source-stream-unsaved';

// «Пустая» ROI при сбросе зон (поворот инвалидирует зоны) — как дефолт модалки
const DEFAULT_EMPTY_ROI = {
  x: 0, y: 0, width: 0, height: 0, mode: 'abs',
} as const;

function SourceStreamPage(): React.JSX.Element {
  const { t } = useSourceStreamPageTranslation();
  const streamId = useStreamIdFromLocation();

  const getStream = useGetStream(streamId, { enriched: 0 });
  const stream = getStream.data;

  // Композит (группа + личные): только он содержит унаследованные ВА —
  // enriched=0 отдаёт лишь личные параметры потока
  const getEnrichedStream = useGetStream(streamId, { enriched: 1 });
  const enrichedStream = getEnrichedStream.data;

  // Группа потока: отдельный запрос targets=['groups'] (groups[0] = group_id)
  const groupId = useStreamGroupId(streamId);

  const getGroup = useGetGroup(groupId ?? '', {
    query: { enabled: !!groupId },
  });
  const group = groupId ? getGroup.data : undefined;

  const methods = useForm<StreamSettingsFormValues>({
    defaultValues: {
      name: '',
      description: '',
      group_id: '',
      data: {
        type: 'stream',
        reference: '',
        timestamp_source: undefined,
        pts: { start_time: '' },
        rotation: undefined,
      },
      autorestart: { restart: undefined, attempt_count: '', delay: '' },
      location: undefined,
      analytics: [],
    },
  });
  const {
    formState: { isDirty, dirtyFields },
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
  } = methods;
  const { append } = useFieldArray({ control: methods.control, name: 'analytics' });

  const [activeSection, setActiveSection] = useState<SourceStreamSection>('main');
  const [sectionErrors, setSectionErrors] = useState<Partial<Record<SourceStreamSection, boolean>>>({});

  const putStream = useUpdateStream();

  // Ключи личных ВА потока (enriched=0) — по ним отличаем унаследованные ВА композита
  const ownAnalyticsKeys = useMemo(
    () => new Set(
      (stream?.analytics ?? [])
        .map((item) => normalizeStreamAnalyticsRecordItem(item))
        .map((item) => item.label ?? item.type)
        .filter((key): key is string => !!key),
    ),
    [stream],
  );

  useEffect(() => {
    if (!stream) return;

    // Не перезаписываем грязную форму фоновым рефетчем (фокус окна, инвалидация):
    // reset затирал бы несохранённые правки
    if (isDirty) return;

    // После успешного сохранения isDirty сбрасывается раньше, чем приходит свежий
    // getStream, а во время рефетча в кэше ещё лежат данные до PUT —
    // не затираем форму устаревшими значениями
    if (getStream.isFetching || getEnrichedStream.isFetching) return;

    const formSettings = toFormStreamSettings({
      data: stream.data,
      autorestart: stream.autorestart,
      location: stream.location,
    });

    // ВА формы — композит из enriched: унаследованные от группы первыми, личные за ними.
    // Композит может быть недоступен (ошибка запроса) — фолбэк на личные ВА потока.
    const composedAnalytics = ((enrichedStream?.analytics ?? stream.analytics) ?? [])
      .map(normalizeStreamAnalyticsRecordItem) as unknown as StreamAnalytics[];
    const formAnalytics = [
      ...composedAnalytics.filter((item) => !ownAnalyticsKeys.has(item.label ?? item.type)),
      ...composedAnalytics.filter((item) => ownAnalyticsKeys.has(item.label ?? item.type)),
    ];

    reset({
      name: stream.name ?? '',
      description: stream.description ?? '',
      group_id: groupId ?? '',
      data: { ...formSettings.data, type: formSettings.data.type ?? 'stream' },
      autorestart: formSettings.autorestart,
      location: formSettings.location,
      // ВА потока: тип нормализуется из type/analytic_name (ZEN #9 — граница апи и формы)
      analytics: formAnalytics,
    });
  }, [stream, enrichedStream, ownAnalyticsKeys, groupId, reset, isDirty,
    getStream.isFetching, getEnrichedStream.isFetching]);

  useUnsavedChangesToast(isDirty, t('unsaved_toast'), UNSAVED_TOAST_ID);

  const analytics = watch('analytics');

  const settingsSyncStatus = useMemo(
    () => getStreamSettingsSyncStatus(group, stream),
    [group, stream],
  );

  // Пофилдовые статусы синхронизации параметров ВА с группой (по индексам формы)
  const analyticsParamsSyncStatus = useMemo(() => {
    const map: Record<number, Record<string, boolean>> = {};

    (analytics ?? []).forEach((item, index) => {
      map[index] = getStreamAnalyticsParamsSyncStatus(group, stream, item);
    });

    return map;
  }, [analytics, group, stream]);

  const analyticsNavItems: SourceStreamAnalyticsItem[] = (analytics ?? []).map((item, index) => {
    const labelParts = parseAnalyticsLabel(item.label);
    const title = getAnalyticsTypeLabel(item.type)
      + (labelParts ? ` ${labelParts.number}` : '');

    // Статус ВА целиком: зелёная — все параметры совпадают с группой,
    // серая — есть расхождения; личная ВА без аналога в группе — без иконки
    const paramsStatus = analyticsParamsSyncStatus[index] ?? {};
    let syncStatus: SourceStreamAnalyticsItem['syncStatus'];
    if (Object.keys(paramsStatus).length > 0) {
      syncStatus = Object.values(paramsStatus).every(Boolean) ? 'synced' : 'unsynced';
    }

    return { key: `va:${index}`, title, syncStatus };
  });

  const addAnalytics = (type: string) => {
    const current = getValues('analytics') ?? [];
    const item = buildDefaultStreamAnalytics(type, nextAnalyticsLabel(current, type));

    append(item);
    setActiveSection(`va:${current.length}`);
  };

  // Активная секция ВА исчезла (удаление) — возвращаемся на «Основные параметры»
  useEffect(() => {
    if (!activeSection.startsWith('va:')) return;

    const index = Number(activeSection.slice(3));
    if (!analytics?.[index]) setActiveSection('main');
  }, [activeSection, analytics]);

  // Зоны ROI/DROI — механика кнопки «Настроить зоны» (макет GUI-3863):
  // ВА выбрана → модалка сразу; не выбрана → список ВА, по выбору — переход + модалка.
  // Рестрим запрашивается только при открытии модалки; при ошибке — текст под кнопкой
  const [zonesModalIndex, setZonesModalIndex] = useState<number>();
  const [pendingZonesIndex, setPendingZonesIndex] = useState<number>();
  const [zonesMenuOpen, setZonesMenuOpen] = useState(false);
  const [zonesError, setZonesError] = useState(false);

  const streamRotation = watch('data.rotation');

  const { retransmission, status: restreamStatus } = useStreamRestream(
    streamId,
    undefined,
    pendingZonesIndex !== undefined,
  );

  useEffect(() => {
    if (pendingZonesIndex === undefined) return;

    if (restreamStatus === 'success') {
      setZonesModalIndex(pendingZonesIndex);
      setPendingZonesIndex(undefined);
      setZonesError(false);
    } else if (restreamStatus === 'error') {
      setPendingZonesIndex(undefined);
      setZonesError(true);
    }
  }, [pendingZonesIndex, restreamStatus]);

  const openZonesFor = (index: number) => {
    setZonesError(false);
    setPendingZonesIndex(index);
  };

  const onConfigureZones = () => {
    if (activeSection.startsWith('va:')) {
      openZonesFor(Number(activeSection.slice(3)));
      return;
    }

    setZonesMenuOpen((open) => !open);
  };

  const onSelectZonesVa = (key: string) => {
    setZonesMenuOpen(false);

    const index = Number(key.slice(3));
    setActiveSection(`va:${index}`);
    openZonesFor(index);
  };

  const closeZonesModal = () => setZonesModalIndex(undefined);

  const zonesVa = zonesModalIndex !== undefined ? analytics?.[zonesModalIndex] : undefined;

  const zonesModalTitle = zonesModalIndex !== undefined
    ? t('zones_modal_title', { name: analyticsNavItems[zonesModalIndex]?.title ?? '' })
    : undefined;

  const handleEditZonesData = ({ roi, droi, rotationDeg }: TEditData) => {
    if (zonesModalIndex === undefined) return;

    const initialRotation = (streamRotation ?? 0) as TRotationDeg;

    // Смена поворота инвалидирует зоны всех ВА — сбрасываем к дефолтам (как легаси-интеграция)
    if (rotationDeg !== initialRotation) {
      (analytics ?? []).forEach((_, index) => {
        setValue(`analytics.${index}.parameters.parameters.roi`, DEFAULT_EMPTY_ROI, { shouldDirty: true });
        setValue(`analytics.${index}.parameters.parameters.droi`, undefined, { shouldDirty: true });
      });
      setValue('data.rotation', rotationDeg, { shouldDirty: true });
    }

    setValue(`analytics.${zonesModalIndex}.parameters.parameters.roi`, roi, { shouldDirty: true });
    setValue(
      `analytics.${zonesModalIndex}.parameters.parameters.droi`,
      (droi as TRectangle | null) ?? undefined,
      { shouldDirty: true },
    );

    closeZonesModal();
  };

  const saveForm = handleSubmit(async (values) => {
    const requestSettings = toRequestStreamSettings({
      data: values.data,
      autorestart: values.autorestart,
      location: values.location,
    });

    // Нетронутая унаследованная ВА не отправляется: иначе любое сохранение
    // материализовало бы её как личную. Отредактированная (dirty) — уходит в PUT.
    const analyticsDirty = dirtyFields.analytics as unknown[] | undefined;
    const requestAnalytics = (values.analytics ?? []).filter((item, index) => {
      const key = item.label ?? item.type;
      const isUntouchedInherited = !!key && !ownAnalyticsKeys.has(key) && !analyticsDirty?.[index];

      return !isUntouchedInherited;
    });

    try {
      await putStream.mutateAsync({
        streamId,
        data: {
          name: values.name,
          description: values.description,
          group_id: values.group_id,
          ...requestSettings,
          analytics: stripEmpty(requestAnalytics) as StreamAnalytics[],
        } as CreateStreamRequest,
      });

      toast.success(t('save_success'));
      setSectionErrors({});
      reset(getValues());
    } catch (error) {
      const apiError = getApiErrorInfo(error);
      const fieldErrors = parseValidationDetail(apiError.detail);

      if (fieldErrors.length === 0) {
        toast.error(apiError.desc ?? t('save_error'));
        return;
      }

      setServerFieldErrors(setError, fieldErrors);
      setSectionErrors(mapErrorsToSections(fieldErrors));
      toast.error(t('save_validation_error'));
    }
  });

  if (getStream.isLoading) {
    return (
      <Page>
        <PageContent>
          <Loader />
        </PageContent>
      </Page>
    );
  }

  if (getStream.isError || !stream) {
    return (
      <Page>
        <PageContent>
          <div className={st.ErrorBlock}>
            <span>{t('load_error')}</span>
            <Link to={routes.sources.list}>{t('back_to_list')}</Link>
          </div>
        </PageContent>
      </Page>
    );
  }

  let mainContent: React.JSX.Element;
  if (activeSection === 'main') {
    mainContent = (
      <div className={st.MainCard}>
        <SourceStreamGeneralInfo />

        <div className={st.Divider} />

        <SourceStreamSettings syncStatus={settingsSyncStatus} />
      </div>
    );
  } else {
    const index = Number(activeSection.slice(3));
    const analyticsItem = analytics?.[index];
    const key = analyticsItem?.label ?? analyticsItem?.type;
    const isInherited = !!key && !ownAnalyticsKeys.has(key);

    if (analyticsItem) {
      mainContent = (
        <div className={st.MainCard}>
          <FacestreamAnalytic
            allowDelete={!isInherited}
            getSyncStatus={(path) => analyticsParamsSyncStatus[index]?.[path]}
            index={index}
            showRoiDroi
            streamId={streamId}
          />
        </div>
      );
    } else {
      // Элемент исчез — до срабатывания эффекта переключения секции
      mainContent = null;
    }
  }

  const info = (
    <React.Fragment>
      <div className={st.RightCard}>
        <SourceStreamLastFrame
          onConfigureZones={onConfigureZones}
          streamId={streamId}
          zonesError={zonesError}
          zonesLoading={pendingZonesIndex !== undefined}
          zonesMenu={zonesMenuOpen ? { items: analyticsNavItems, onSelect: onSelectZonesVa } : undefined}
        />
        <SourceGeoposition
          defaultValues={watch('location')}
          onSubmit={(next) => {
            setValue('location', next, { shouldDirty: true });
          }}
          syncStatus={settingsSyncStatus.location}
        />
      </div>
      <SourceStreamLegend />
    </React.Fragment>
  );

  return (
    <Page>
      <PageContent>
        <FormProvider {...methods}>
          <SourceStreamPageHeader
            isDirty={isDirty}
            isSaving={putStream.isPending}
            onSave={saveForm}
            streamId={streamId}
          />

          <SideNavLayout
            info={info}
            main={mainContent}
            nav={(
              <SourceStreamSettingsNav
                activeSection={activeSection}
                analyticsItems={analyticsNavItems}
                onAddAnalytics={addAnalytics}
                onChange={setActiveSection}
                sectionErrors={sectionErrors}
              />
            )}
          />

          <UnsavedChangesModal when={isDirty} />

          {!!zonesVa && (
            <RegionsEditModal
              droi={zonesVa.parameters?.parameters?.droi}
              droiFormat="rect"
              handleEditData={handleEditZonesData}
              initialRotationDeg={(streamRotation ?? 0) as TRotationDeg}
              isOpen
              key={zonesModalIndex}
              onClose={closeZonesModal}
              retransmission={retransmission}
              roi={zonesVa.parameters?.parameters?.roi}
              title={zonesModalTitle}
            />
          )}
        </FormProvider>
      </PageContent>
    </Page>
  );
}

export { SourceStreamPage };
